import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid, TextField, MenuItem, Typography, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  FormDialog,
} from '../components';
import { useWarehouses, useCreateWarehouse } from '../hooks/useWarehouses';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, humanize, WAREHOUSE_TYPES } from '../constants';

const emptyForm = {
  name: '', code: '', type: 'warehouse', contactPhone: '',
  street: '', city: '', state: '', postalCode: '', country: '',
};

export default function Warehouses() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);

  const { data = [], isLoading } = useWarehouses();
  const createWarehouse = useCreateWarehouse();

  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(
      (w) => w.name?.toLowerCase().includes(q) || w.code?.toLowerCase().includes(q)
    );
  }, [data, search]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openCreate = () => {
    setForm(emptyForm);
    setError(null);
    setOpen(true);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.name.trim() || !form.code.trim()) {
      setError('Name and code are required.');
      return;
    }
    try {
      await createWarehouse.mutateAsync({
        businessUuid,
        name: form.name,
        code: form.code,
        type: form.type,
        contactPhone: form.contactPhone || undefined,
        address: {
          street: form.street,
          city: form.city,
          state: form.state,
          postalCode: form.postalCode,
          country: form.country,
        },
      });
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Warehouse',
      render: (row) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
            {row.name}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray500 }}>
            {row.code}
          </Typography>
        </>
      ),
    },
    { key: 'type', label: 'Type', render: (row) => <StatusChip status={row.type} /> },
    {
      key: 'isActive',
      label: 'Status',
      render: (row) => <StatusChip status={row.isActive ? 'active' : 'inactive'} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="Warehouses"
        actionLabel="New Warehouse"
        actionIcon={<AddIcon />}
        onAction={openCreate}
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search warehouses..."
      />

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        onRowClick={(row) => history.push(`/inventory-management/warehouses/${row.uuid}`)}
        emptyTitle="No warehouses found"
        emptyMessage="Add your first warehouse."
      />

      <FormDialog
        open={open}
        title="New Warehouse"
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        submitLabel="Create Warehouse"
        loading={createWarehouse.isPending}
      >
        {error && (
          <Typography variant="body2" sx={{ color: colors.error, mb: 1.5 }}>
            {error}
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Name" value={form.name} onChange={setField('name')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Code" value={form.code} onChange={setField('code')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Type" value={form.type} onChange={setField('type')} fullWidth size="small">
              {WAREHOUSE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Phone" value={form.contactPhone} onChange={setField('contactPhone')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" sx={{ color: colors.gray500 }}>ADDRESS</Typography>
            </Divider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Street" value={form.street} onChange={setField('street')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="City" value={form.city} onChange={setField('city')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="State" value={form.state} onChange={setField('state')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Postal Code" value={form.postalCode} onChange={setField('postalCode')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Country" value={form.country} onChange={setField('country')} fullWidth size="small" />
          </Grid>
        </Grid>
      </FormDialog>
    </>
  );
}
